import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import myConfig from "../configs/config";

const PendingOperatorAccessContext = createContext({
  pendingCount: 0,
  pendingItems: [],
  loading: false,
  ready: false,
  error: null,
  refreshPendingAccess: () => {},
  acknowledgeAccess: async () => {},
});

export function usePendingOperatorAccess() {
  return useContext(PendingOperatorAccessContext);
}

export function PendingOperatorAccessProvider({ farm_id, children }) {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const refreshPendingAccess = useCallback(async () => {
    if (!farm_id) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(
        `${myConfig.API_URL}/operator/pending_access/?farm_id=${farm_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      const data = res.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      setPendingItems(items);
      setPendingCount(
        typeof data.count === "number" ? data.count : items.length
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [farm_id]);

  const acknowledgeAccess = useCallback(
    async (operatorId) => {
      if (!farm_id || !operatorId) return;
      await axios.post(
        `${myConfig.API_URL}/operator/${operatorId}/acknowledge_access/?farm_id=${farm_id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      setPendingItems((current) =>
        current.filter((item) => String(item.operatorId) !== String(operatorId))
      );
      setPendingCount((current) => Math.max(0, current - 1));
      await refreshPendingAccess();
    },
    [farm_id, refreshPendingAccess]
  );

  useEffect(() => {
    refreshPendingAccess();
    const interval = setInterval(refreshPendingAccess, 5000);
    return () => clearInterval(interval);
  }, [refreshPendingAccess]);

  return (
    <PendingOperatorAccessContext.Provider
      value={{
        pendingCount,
        pendingItems,
        loading,
        ready,
        error,
        refreshPendingAccess,
        acknowledgeAccess,
      }}
    >
      {children}
    </PendingOperatorAccessContext.Provider>
  );
}
